import { useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { LandingPageSkeleton } from '@/components/skeletons/LandingPageSkeleton';
import { HomeTabSkeleton } from '@/components/skeletons/HomeTabSkeleton';
import { LaunchScreen } from '@/components/loading/LaunchScreen';
import { BrandLogo } from '@/components/brand/BrandLogo';

/**
 * Dashboard shell skeleton - shows header + bottom nav + content area
 * Logo is hard-suppressed until handoff completes via BrandLogo.
 */
const DashboardShellSkeleton = ({ children }: { children: React.ReactNode }) => (
  // E2E stability: protected-content marker ensures tests can sync during Suspense loading
  <div data-testid="protected-content" className="min-h-screen bg-background">
    {/* Header skeleton */}
    <div className="sticky top-0 z-40 bg-background border-b border-border/40">
      <div className="flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          {/* BrandLogo hard-suppressed during handoff - prevents skeleton logo flash */}
          <BrandLogo 
            sizeClassName="w-8 h-8" 
            tracerId="skeleton-header-logo"
            tracerFile="RouteAwareLoadingScreen.tsx"
          />
          <Skeleton className="h-5 w-24" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-9 rounded-full" />
          <Skeleton className="h-9 w-20 rounded-full" />
        </div>
      </div>
    </div>

    {/* Content area */}
    <main className="pb-20 px-3 pt-4 max-w-4xl mx-auto">
      {children}
    </main>

    {/* Bottom nav skeleton */}
    <div className="fixed bottom-0 left-0 right-0 h-[calc(4rem+env(safe-area-inset-bottom,0px))] bg-background border-t border-border/40 flex items-center justify-around px-4 footer-safe">
      {['Home', 'Track', 'Meds', 'Appts', 'Beta', 'More'].map((label) => (
        <div key={label} className="flex flex-col items-center gap-1">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-2 w-8" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Legal/info page skeleton
 */
const LegalPageSkeleton = () => (
  <DashboardShellSkeleton>
    <Card className="bg-gradient-to-br from-primary/5 to-primary/3">
      <CardContent className="p-6 space-y-4">
        <Skeleton className="h-8 w-64 bg-primary/10" />
        <Skeleton className="h-4 w-32 bg-primary/10" />
        <div className="space-y-3 pt-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full bg-primary/10" />
          ))}
        </div>
      </CardContent>
    </Card>
  </DashboardShellSkeleton>
);

/**
 * Settings page skeleton
 */
const SettingsPageSkeleton = () => (
  <DashboardShellSkeleton>
    <div className="space-y-4">
      <Skeleton className="h-8 w-32 bg-primary/10" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Card key={i} className="bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg bg-primary/10" />
            <div className="flex-1">
              <Skeleton className="h-5 w-32 bg-primary/10 mb-1" />
              <Skeleton className="h-3 w-48 bg-primary/10" />
            </div>
            <Skeleton className="h-5 w-5 bg-primary/10" />
          </CardContent>
        </Card>
      ))}
    </div>
  </DashboardShellSkeleton>
);

/**
 * Auth page skeleton - matches root loading for seamless transition
 */
const AuthPageSkeleton = () => (
  // E2E stability: auth-loading marker for auth page skeleton
  <div data-testid="auth-loading">
    <LaunchScreen />
  </div>
);

/**
 * Route-aware loading screen that shows appropriate skeleton based on destination
 */
export const RouteAwareLoadingScreen = () => {
  const location = useLocation();
  const path = location.pathname;
  const tab = new URLSearchParams(location.search).get('tab');
  
  // Auth page
  if (path === '/auth') {
    return <AuthPageSkeleton />;
  }
  
  // Marketing/landing page
  if (path === '/marketing') {
    return <LandingPageSkeleton />;
  }
  
  // Legal pages
  if (['/terms', '/privacy', '/data-breach-policy', '/medical-disclaimer', '/ai-wellness-disclaimer', '/faq'].some(p => path.startsWith(p))) {
    return <LegalPageSkeleton />;
  }
  
  // Settings page
  if (path === '/settings') {
    return <SettingsPageSkeleton />;
  }
  
  // Dashboard home tab - use specialized skeleton
  if (path === '/dashboard' && (!tab || tab === 'home')) {
    return (
      <DashboardShellSkeleton>
        <HomeTabSkeleton />
      </DashboardShellSkeleton>
    );
  }
  
  // Default dashboard skeleton for other protected routes
  return (
    <DashboardShellSkeleton>
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="bg-gradient-to-br from-primary/5 to-primary/3">
            <CardContent className="p-4">
              <Skeleton className="h-6 w-48 bg-primary/10 mb-2" />
              <Skeleton className="h-4 w-full bg-primary/10" />
              <Skeleton className="h-4 w-3/4 bg-primary/10 mt-1" />
            </CardContent>
          </Card>
        ))}
      </div>
    </DashboardShellSkeleton>
  );
};
